/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable unused-imports/no-unused-vars */
import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import { EnhancedSectionData } from '../../types/editor';
import { useDebouncedUpdate } from '../../hooks/useDebouncedUpdate';
import { useCKEditorStyleExtractor } from '../../utils/style-extractor';
import { applyOpacityToColor } from '../../utils/common';
import { Trash } from '../../icons/trash';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface FreeElementProps {
  element: EnhancedSectionData;
  isSelected: boolean;
  canvasBounds: { width: number; height: number };
  onSelect: () => void;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, x: number, y: number, width: number, height: number) => void;
  onRotate: (id: string, rotation: number) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, content: string, extra?: any) => void;
  onBringForward: (id: string) => void;
  onSendBackward: (id: string) => void;
  placeholders?: Array<{ value: string; label: string }>;
  children?: React.ReactNode;
  hideRotate?: boolean;
  onMoveToPage?: (id: string, offset: -1 | 1) => void;
  pageIndex?: number;
  totalPages?: number;
}

const editableTypes = new Set(['text', 'text-block', 'heading', 'heading-2', 'heading-3', 'heading-4']);

const RESIZE_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;
type ResizeHandle = typeof RESIZE_HANDLES[number];

const handleCursorMap: Record<ResizeHandle, string> = {
  nw: 'nw-resize', n: 'n-resize', ne: 'ne-resize',
  e: 'e-resize', se: 'se-resize', s: 's-resize',
  sw: 'sw-resize', w: 'w-resize',
};

const handlePositions: Record<ResizeHandle, React.CSSProperties> = {
  nw: { top: -5, left: -5 },
  n: { top: -5, left: '50%', transform: 'translateX(-50%)' },
  ne: { top: -5, right: -5 },
  e: { top: '50%', right: -5, transform: 'translateY(-50%)' },
  se: { bottom: -5, right: -5 },
  s: { bottom: -5, left: '50%', transform: 'translateX(-50%)' },
  sw: { bottom: -5, left: -5 },
  w: { top: '50%', left: -5, transform: 'translateY(-50%)' },
};

const ASPECT_RATIO_TYPES = new Set(['image', 'qr-code']);

const FreeElement: React.FC<FreeElementProps> = ({
  element,
  isSelected,
  canvasBounds,
  onSelect,
  onMove,
  onResize,
  onRotate,
  onDelete,
  onUpdate,
  onBringForward,
  onSendBackward,
  placeholders = [],
  children,
  hideRotate = false,
  onMoveToPage,
  pageIndex = 0,
  totalPages = 1,
}) => {
  const { extractStyles, getSingleStyles } = useCKEditorStyleExtractor();
  const [isEditing, setIsEditing] = useState(false);
  const [localContent, setLocalContent] = useState(element.content);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const contentEditableRef = useRef<HTMLDivElement | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  // Continuously measure table element rendered DOM height
  useEffect(() => {
    const el = contentEditableRef.current;
    if (!el) return;

    const measure = () => {
      const table = el.querySelector('table');
      const target = table || (el.firstElementChild as HTMLElement) || el;
      const rect = target.getBoundingClientRect();
      const h = Math.round(rect.height || target.scrollHeight || el.scrollHeight);
      if (h > 0) {
        setMeasuredHeight(prev => (prev !== h ? h : prev));
      }
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        measure();
      });
      ro.observe(el);
      const table = el.querySelector('table');
      if (table) ro.observe(table);
      return () => ro.disconnect();
    }
  }, [localContent, element.attributes, element.type]);

  // Sync measured height back to editor state if element.height is outdated/undersized
  useEffect(() => {
    if (element.type === 'table' && measuredHeight && measuredHeight > 0) {
      if (Math.abs((element.height || 0) - measuredHeight) > 2) {
        onResize(element.id, element.x, element.y, element.width, measuredHeight);
      }
    }
  }, [element.id, element.type, element.x, element.y, element.width, element.height, measuredHeight, onResize]);

  const effectiveHeight = useMemo(() => {
    if (element.type === 'table') {
      return measuredHeight ? Math.max(element.height || 0, measuredHeight) : (element.height || 78);
    }
    if (element.type === 'divider') {
      return Math.max(element.height || 0, 20);
    }
    return element.height;
  }, [element.type, element.height, measuredHeight]);

  const handleUpdate = useCallback((content: string, extra?: any) => {
    onUpdate(element.id, content, extra);
  }, [onUpdate, element.id]);

  const debouncedUpdate = useDebouncedUpdate(handleUpdate, 300);

  // Sync external content changes when not actively typing
  React.useEffect(() => {
    if (!isEditing) {
      setLocalContent(element.content);
    }
  }, [element.content, isEditing]);

  const commitEdit = useCallback(() => {
    if (contentEditableRef.current) {
      const newContent = contentEditableRef.current.innerHTML;
      setLocalContent(newContent);
      const styles = extractStyles(element.content, { includeTagStyles: true, includeInlineStyles: true, mergeNestedStyles: true, filterEmptyStyles: true });
      const single = getSingleStyles(styles);
      const mergedAttributes = { ...(element.attributes || {}), styles: { ...(element.attributes?.styles || {}), ...single } };
      onUpdate(element.id, newContent, { attributes: mergedAttributes });
    } else if (elementRef.current) {
      // For elements with children (e.g. built-in stationery Editable)
      const editableChildren = elementRef.current.querySelectorAll('[contenteditable="true"]');
      editableChildren.forEach((child: any) => {
        child.blur();
      });
    }
  }, [element.id, element.content, element.attributes, extractStyles, getSingleStyles, onUpdate]);

  // When element loses selection, automatically commit and exit editing mode
  useEffect(() => {
    if (!isSelected && isEditing) {
      commitEdit();
      setIsEditing(false);
    }
  }, [isSelected, isEditing, commitEdit]);

  // When editing, clicking anywhere outside the element immediately commits and exits editing mode
  useEffect(() => {
    if (!isEditing) return;

    const handleWindowPointerDown = (e: PointerEvent) => {
      if (elementRef.current && !elementRef.current.contains(e.target as Node)) {
        commitEdit();
        setIsEditing(false);
      }
    };

    window.addEventListener('pointerdown', handleWindowPointerDown, true);
    return () => {
      window.removeEventListener('pointerdown', handleWindowPointerDown, true);
    };
  }, [isEditing, commitEdit]);

  // Focus editable text when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const target = contentEditableRef.current || (elementRef.current?.querySelector('[contenteditable="true"]') as HTMLElement);
      if (target) {
        target.focus();
        try {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(target);
          range.collapse(false); // collapse to end
          selection?.removeAllRanges();
          selection?.addRange(range);
        } catch (err) {
          console.error(err);
        }
      }
    }
  }, [isEditing]);

  // ─── MOVE ───────────────────────────────────────────────────────────────────
  const handleBodyPointerDown = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('[data-control]')) return;

    if (isEditing) {
      const target = e.target as HTMLElement;
      const isInsideEditable = target.isContentEditable || target.closest('[contenteditable="true"]') || target === contentEditableRef.current;
      if (isInsideEditable) {
        // Interacting with text content
        return;
      }
      // Clicked on element boundary or outer padding — exit editing and allow drag
      commitEdit();
      setIsEditing(false);
    }

    e.preventDefault();
    e.stopPropagation();
    onSelect();

    const startX = e.clientX;
    const startY = e.clientY;
    const startElX = element.x;
    const startElY = element.y;

    const maxX = canvasBounds.width > 0 ? Math.max(0, canvasBounds.width - element.width) : Infinity;
    const maxY = canvasBounds.height > 0 ? Math.max(0, canvasBounds.height - element.height) : Infinity;

    const onMove_ = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const newX = Math.max(0, Math.min(maxX, startElX + dx));
      const newY = Math.max(0, Math.min(maxY, startElY + dy));
      onMove(element.id, newX, newY);
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove_);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove_);
    window.addEventListener('pointerup', onUp);
  }, [isEditing, commitEdit, element.x, element.y, element.width, element.height, canvasBounds, onMove, onSelect, element.id]);

  // ─── RESIZE ─────────────────────────────────────────────────────────────────
  const handleResizePointerDown = useCallback((handle: ResizeHandle, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startElX = element.x;
    const startElY = element.y;
    const startW = element.width;
    const startH = effectiveHeight;
    const aspectRatio = startW / startH;
    const lockAspect = ASPECT_RATIO_TYPES.has(element.type);

    const onMove_ = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      let newX = startElX, newY = startElY, newW = startW, newH = startH;

      switch (handle) {
        case 'se': newW = Math.max(40, startW + dx); newH = lockAspect ? newW / aspectRatio : Math.max(20, startH + dy); break;
        case 's': newH = Math.max(20, startH + dy); break;
        case 'e': newW = Math.max(40, startW + dx); break;
        case 'sw': newW = Math.max(40, startW - dx); newX = startElX + (startW - newW); newH = lockAspect ? newW / aspectRatio : Math.max(20, startH + dy); break;
        case 'w': newW = Math.max(40, startW - dx); newX = startElX + (startW - newW); break;
        case 'nw': newW = Math.max(40, startW - dx); newH = lockAspect ? newW / aspectRatio : Math.max(20, startH - dy); newX = startElX + (startW - newW); newY = startElY + (startH - newH); break;
        case 'n': newH = Math.max(20, startH - dy); newY = startElY + (startH - newH); break;
        case 'ne': newW = Math.max(40, startW + dx); newH = lockAspect ? newW / aspectRatio : Math.max(20, startH - dy); newY = startElY + (startH - newH); break;
      }

      onResize(element.id, newX, newY, newW, newH);
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove_);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove_);
    window.addEventListener('pointerup', onUp);
  }, [element, effectiveHeight, onResize]);

  // ─── ROTATE ─────────────────────────────────────────────────────────────────
  const handleRotatePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const el = elementRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
    const startRotation = element.rotation;

    const onMove_ = (ev: PointerEvent) => {
      const currentAngle = Math.atan2(ev.clientY - cy, ev.clientX - cx) * (180 / Math.PI);
      const delta = currentAngle - startAngle;
      onRotate(element.id, (startRotation + delta + 360) % 360);
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove_);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove_);
    window.addEventListener('pointerup', onUp);
  }, [element.rotation, element.id, onRotate]);

  // ─── EDIT (text elements) ────────────────────────────────────────────────────
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editableTypes.has(element.type)) return;
    setIsEditing(true);

    const target = e.target as HTMLElement;
    const editableTarget = target.isContentEditable ? target : (target.closest('[contenteditable="true"]') as HTMLElement);
    if (editableTarget) {
      setTimeout(() => {
        editableTarget.focus();
        try {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(editableTarget);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        } catch (err) {
          console.error(err);
        }
      }, 0);
    }
  }, [element.type]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (isEditing) {
      if (elementRef.current && elementRef.current.contains(e.relatedTarget as Node)) {
        return;
      }
      commitEdit();
      setIsEditing(false);
    }
  }, [isEditing, commitEdit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isEditing) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      commitEdit();
      setIsEditing(false);
    } else if (e.key === 'Enter' && !e.shiftKey && element.type !== 'text-block' && !children) {
      e.preventDefault();
      e.stopPropagation();
      commitEdit();
      setIsEditing(false);
    }
  }, [isEditing, commitEdit, element.type, children]);

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  const contentNode = useMemo(() => {
    if (children) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            pointerEvents: isEditing ? 'auto' : 'none',
            '& img': {
              userDrag: 'none',
              WebkitUserDrag: 'none',
              pointerEvents: 'none',
            },
          }}
          onBlur={(e) => {
            if (elementRef.current && !elementRef.current.contains(e.relatedTarget as Node)) {
              commitEdit();
              setIsEditing(false);
            }
          }}
        >
          {children}
        </Box>
      );
    }

    return (
      <Box
        ref={contentEditableRef}
        dangerouslySetInnerHTML={{ __html: localContent || '<p style="color:#aaa;font-size:13px;margin:0">Double-click to edit…</p>' }}
        contentEditable={isEditing}
        suppressContentEditableWarning={true}
        onBlur={handleBlur}
        sx={{
          width: '100%',
          height: element.type === 'table' ? 'auto' : '100%',
          minHeight: element.type === 'table' ? 'auto' : '100%',
          overflow: element.type === 'buttons' || element.type === 'signature' || element.type === 'table' || editableTypes.has(element.type) ? 'visible' : 'hidden',
          pointerEvents: isEditing ? 'auto' : 'none',
          wordBreak: 'break-word',
          whiteSpace: element.type === 'table' ? 'normal' : 'pre-wrap',
          outline: 'none',
          cursor: isEditing ? 'text' : 'inherit',
          display: editableTypes.has(element.type) || element.type === 'table' ? 'block' : 'flex',
          flexDirection: editableTypes.has(element.type) || element.type === 'table' ? undefined : 'column',
          justifyContent: editableTypes.has(element.type) ? undefined : (element.type === 'table' ? 'flex-start' : 'center'),
          alignItems: editableTypes.has(element.type) ? undefined : (element.type === 'table' ? 'stretch' : 'center'),
          '& p, & h1, & h2, & h3, & h4, & h5, & h6': {
            margin: 0,
            padding: 0,
            width: '100% !important',
            height: '100% !important',
            boxSizing: 'border-box'
          },
          '& hr': {
            width: '100%',
            margin: 0
          },
          '& a': {
            width: '100% !important',
            height: '100% !important',
            boxSizing: 'border-box'
          },
          '& img': { maxWidth: '100%', maxHeight: '100%', objectFit: element.attributes?.styles?.objectFit || 'contain', userDrag: 'none', WebkitUserDrag: 'none' },
          '& > div': {
            width: '100% !important',
            height: element.type === 'table' ? 'auto !important' : undefined,
            boxSizing: 'border-box'
          },
          '& table': { width: '100% !important', height: 'auto !important', borderCollapse: 'collapse', whiteSpace: 'normal', tableLayout: 'fixed', margin: '0 !important', backgroundColor: 'transparent' },
          '& table tr': { boxSizing: 'border-box' },
          '& table td': {
            boxSizing: 'border-box',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            verticalAlign: 'middle',
            backgroundColor: applyOpacityToColor(
              element.attributes?.styles?.tBody?.backgroundColor || 'rgba(255, 255, 255, 0.75)',
              typeof element.attributes?.styles?.opacity === 'number' ? element.attributes.styles.opacity : 1
            ),
          },
          '& table th': {
            boxSizing: 'border-box',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            verticalAlign: 'middle',
            backgroundColor: applyOpacityToColor(
              element.attributes?.styles?.tHead?.backgroundColor || 'rgba(245, 245, 245, 0.75)',
              typeof element.attributes?.styles?.opacity === 'number' ? element.attributes.styles.opacity : 1
            ),
          },
          '& svg': { display: 'block', width: '100% !important', height: '100% !important' },
        }}
      />
    );
  }, [isEditing, localContent, element.id, element.type, handleBlur, children, handleKeyDown, JSON.stringify(element.attributes?.styles)]);

  const rotationTransform = `rotate(${element.rotation}deg)`;

  return (
    <Box
      ref={elementRef}
      tabIndex={-1}
      data-free-element-id={element.id}
      onPointerDown={handleBodyPointerDown}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      sx={{
        position: 'absolute',
        left: element.x,
        top: element.y,
        width: element.width,
        height: effectiveHeight,
        transform: rotationTransform,
        zIndex: element.zIndex ?? 1,
        cursor: isEditing ? 'text' : 'move',
        userSelect: isEditing ? 'text' : 'none',
        outline: isSelected ? '1px solid #000' : '1px dashed transparent',
        outlineOffset: '0px',
        borderRadius: '0px',
        '&:hover': {
          outline: isSelected ? '1px solid #000' : '1px dashed #000',
        },
        boxSizing: 'border-box',
        overflow: 'visible',
      }}
    >
      {/* Content */}
      <Box sx={{ width: '100%', height: element.type === 'table' ? 'auto' : '100%', overflow: 'visible', borderRadius: '2px', bgcolor: 'transparent' }}>
        {contentNode}
      </Box>

      {/* Selected controls — toolbar + handles */}
      {isSelected && !isEditing && (
        <>
          {/* Top toolbar — Move actions + Delete, positioned top-right */}
          <Box
            data-control="true"
            sx={{
              position: 'absolute',
              top: -34,
              right: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              zIndex: 1000,
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {onMoveToPage && pageIndex > 0 && (
              <IconButton
                size="small"
                data-control="true"
                onClick={(e) => { e.stopPropagation(); onMoveToPage(element.id, -1); }}
                title="Move to Previous Page"
                sx={{ color: '#fff', bgcolor: '#000', p: '4px', borderRadius: '4px', '&:hover': { bgcolor: '#333' } }}
              >
                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {onMoveToPage && pageIndex < totalPages - 1 && (
              <IconButton
                size="small"
                data-control="true"
                onClick={(e) => { e.stopPropagation(); onMoveToPage(element.id, 1); }}
                title="Move to Next Page"
                sx={{ color: '#fff', bgcolor: '#000', p: '4px', borderRadius: '4px', '&:hover': { bgcolor: '#333' } }}
              >
                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <IconButton
              size="small"
              data-control="true"
              onClick={(e) => { e.stopPropagation(); onDelete(element.id); }}
              title="Delete Element"
              sx={{ color: '#fff', bgcolor: '#ef4444', p: '4px', borderRadius: '4px', '&:hover': { bgcolor: '#dc2626' } }}
            >
              <Trash sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Resize handles — filled circles */}
          {RESIZE_HANDLES.map((handle) => {
            if (element.type === 'divider' && (handle === 'n' || handle === 's')) return null;
            return (
              <Box
                key={handle}
                data-control="true"
                onPointerDown={(e) => { e.stopPropagation(); handleResizePointerDown(handle, e); }}
                sx={{
                  position: 'absolute',
                  width: 10,
                  height: 10,
                  bgcolor: '#000',
                  border: '1px solid #fff',
                  borderRadius: '50%',
                  cursor: handleCursorMap[handle],
                  zIndex: 1001,
                  ...handlePositions[handle],
                }}
              />
            );
          })}

          {/* Rotation handle — just the SVG icon */}
          {!hideRotate && (
            <Box
              data-control="true"
              onPointerDown={(e) => { e.stopPropagation(); handleRotatePointerDown(e); }}
              sx={{
                position: 'absolute',
                bottom: -24,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 16,
                height: 16,
                cursor: 'grab',
                zIndex: 1001,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#000',
                '&:active': { cursor: 'grabbing' },
                '&::before': {
                  content: '""',
                  display: 'block',
                  width: '1px',
                  height: '8px',
                  bgcolor: '#000',
                  position: 'absolute',
                  top: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                },
              }}
            >
              {/* Rotate arrow SVG */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ pointerEvents: 'none' }}
              >
                <path d="M21.5 2v6h-6" />
                <path d="M21.34 15.57a10 10 0 1 1-.57-8.38" />
              </svg>
            </Box>
          )}
        </>
      )}

    </Box>
  );
};

export default FreeElement;
